import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  MessageSquareMore,
  IdCard,
  IndianRupee,
} from "lucide-react";

interface ProductsViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProductsView = ({ isOpen, onClose }: ProductsViewProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const products = [
    // {
    //   title: "Payroll",
    //   description:
    //     "Comprehensive payroll management with automated tax compliance and employee self-service.",
    //   icon: DollarSign,
    //   href: "/products/payroll",
    // },
    {
      title: "Payroll",
      description:
        "simplify HR while  empowering teams and ensuring compliance.",
      icon: IndianRupee,
      href: "/products/Payroll",
    },
    {
      title: "Communication Tool",
      description:
        "Unified internal communication platform for messaging, announcements, and collaboration.",
      icon: MessageSquareMore,
      href: "/products/communicationTool",
    },
    {
      title: "Modern Hiring",
      description:
        "Unified internal communication platform for messaging, announcements, and collaboration.",
      icon: IdCard,
      href: "/products/ModernHiring",
    },
  ];

  const handleProductClick = (href: string) => {
    navigate(href);
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border shadow-xl z-[60]"
      onMouseEnter={() => {}}
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <h3 className="text-lg font-semibold text-foreground mb-6">
          Our Products
        </h3>

        {/* Mobile Layout */}
        <div className="block md:hidden">
          <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
            {products.map((product, index) => {
              const isPrimary = index % 2 === 0;
              return (
                <button
                  key={index}
                  onClick={() => handleProductClick(product.href)}
                  className={`w-full group text-left px-3 py-3 text-sm text-foreground ${
                    isPrimary
                      ? "hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                      : "hover:text-secondary hover:bg-secondary/5 hover:border-secondary/20"
                  } transition-all duration-200 rounded-md border border-transparent flex items-center gap-3`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-md ${
                      isPrimary ? "bg-primary/10" : "bg-secondary/10"
                    } flex-shrink-0`}
                  >
                    <product.icon
                      className={`h-4 w-4 ${
                        isPrimary
                          ? "text-primary shadow-glow"
                          : "text-secondary"
                      }`}
                    />
                  </div>

                  <div
                    className={`font-medium text-sm leading-tight ${
                      isPrimary
                        ? "group-hover:text-primary"
                        : "group-hover:text-secondary"
                    } transition-colors`}
                  >
                    {product.title}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, index) => {
            const isPrimary = index % 2 === 0;
            return (
              <div key={index} className="h-full flex flex-col">
                <button
                  onClick={() => handleProductClick(product.href)}
                  className={`w-full group text-left px-3 py-3 text-sm text-foreground ${
                    isPrimary
                      ? "hover:text-primary hover:bg-primary/5 hover:border-primary/20"
                      : "hover:text-secondary hover:bg-secondary/5 hover:border-secondary/20"
                  } transition-all duration-200 rounded-md border border-transparent flex items-center gap-3`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0 ${
                      isPrimary ? "bg-primary/10" : "bg-secondary/10"
                    }`}
                  >
                    <product.icon
                      className={`h-4 w-4 ${
                        isPrimary
                          ? "text-primary shadow-glow"
                          : "text-secondary"
                      }`}
                    />
                  </div>

                  <div
                    className={`font-medium text-sm leading-tight ${
                      isPrimary
                        ? "group-hover:text-primary"
                        : "group-hover:text-secondary"
                    } transition-colors`}
                  >
                    {product.title}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductsView;
